import React, { useCallback, useEffect, useState } from "react";
import { Alert, Button, Card, Popconfirm, Spin, Tag, Typography, message } from "antd";
import { useTranslation } from "react-i18next";
import { errorText } from "../api";
import { formatDate } from "../shared/money";
import Amount from "../shared/Amount";
import ResponsiveTable from "../../../../design-system/ResponsiveTable";
import { createSofortmeldung, employeesChecklist } from "./api";

const FIELD_LABELS = {
  sozialversicherungsnummer: "SV number",
  steuerId: "Steuer-ID",
  krankenkasse: "Krankenkasse",
  personengruppe: "Personengruppe",
  taetigkeitsschluessel: "Tätigkeitsschlüssel",
  eintrittsdatum: "Entry date",
  iban: "IBAN",
  steuerklasse: "Steuerklasse / ELStAM"
};

const EmployeeChecklist = () => {
  const { t } = useTranslation("accounting");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await employeesChecklist();
      setRows(res.data.rows || []);
    } catch (err) {
      message.error(errorText(err));
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const sofort = async id => {
    try {
      await createSofortmeldung(id);
      message.success(
        t("Sofortmeldung created (download it under Filings and upload it at the SV-Meldeportal)")
      );
      load();
    } catch (err) {
      message.error(errorText(err));
    }
  };

  const incomplete = rows.filter(r => !r.complete && r.employee.status === "Active").length;
  const columns = [
    {
      title: t("Employee"),
      dataIndex: ["employee", "name"],
      mobile: "title",
      render: (v, r) => (
        <span className="acct-wrap">
          {v}{" "}
          {r.employee.employeeId ? (
            <Typography.Text type="secondary">({r.employee.employeeId})</Typography.Text>
          ) : null}{" "}
          {r.employee.status !== "Active" ? <Tag>{r.employee.status}</Tag> : null}
        </span>
      )
    },
    { title: t("Entry date"), dataIndex: ["employee", "startDate"], render: formatDate },
    {
      title: t("Missing payroll data"),
      dataIndex: "missing",
      mobile: "subtitle",
      render: (missing, r) =>
        r.complete ? (
          <Tag color="green">{t("complete")}</Tag>
        ) : (
          missing.map(m => (
            <Tag color="red" key={m}>
              {t(FIELD_LABELS[m] || m)}
            </Tag>
          ))
        )
    },
    {
      title: t("Steuerklasse source"),
      dataIndex: ["elstam", "source"],
      render: v => (v === "elstam" ? "ELStAM" : t("manual"))
    },
    {
      title: t("Sofortmeldung"),
      dataIndex: "sofortmeldung",
      render: (s, r) => {
        if (!s.required)
          return <Typography.Text type="secondary">{t("not required")}</Typography.Text>;
        if (s.done) return <Tag color="green">{formatDate(s.at)}</Tag>;
        return (
          <Popconfirm
            title={t("Create the DEÜV Sofortmeldung (Meldegrund 20) for this employee?")}
            onConfirm={() => sofort(r.employee.id)}
          >
            <Button size="small" danger>
              {t("Create Sofortmeldung")}
            </Button>
          </Popconfirm>
        );
      }
    },
    {
      title: t("Cost rate / h"),
      dataIndex: "costRatePerHour",
      align: "end",
      render: v => (v ? <Amount cents={v} /> : "—")
    }
  ];

  return (
    <Spin spinning={loading}>
      {incomplete ? (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
          message={t(
            "{{count}} active employee(s) have incomplete payroll master data. Complete them in the employee profile before the next Lohnlauf.",
            { count: incomplete }
          )}
        />
      ) : null}
      <Card
        size="small"
        title={t("Employee checklist")}
        extra={<Button onClick={load}>{t("Refresh")}</Button>}
      >
        <ResponsiveTable
          rowKey={r => r.employee.id}
          size="small"
          pagination={false}
          dataSource={rows}
          columns={columns}
        />
      </Card>
    </Spin>
  );
};

export default EmployeeChecklist;
